/**
 * Login page component - Clean, minimal design with shadcn components
 *
 * Supports two modes:
 *   1. Normal login: /login (no special query params)
 *   2. OAuth mode:   /login?mode=oauth&oauth_callback=...&client_id=...&code_challenge=...&redirect_uri=...&state=...
 *      Used when the MCP server redirects here for OAuth authorization.
 */

import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Logo from '../ui/logo';
import { AnimatedBackground } from '../ui/animated-background';
import { LoginForm } from './LoginForm';
import type { OAuthParams } from './LoginForm';
import { useAuth } from '../../hooks/useAuth';
import { safeRedirect } from './safeRedirect';

export const LoginPage: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const redirectTo = safeRedirect(searchParams.get('redirect'), window.location.origin);
  const oauthMode = searchParams.get('mode') === 'oauth';
  const { user, loading } = useAuth();

  // Already signed in (a bookmark, or a tab sent here while its sign-in was briefly unreadable): go on.
  // The MCP OAuth flow needs this page even when signed in.
  useEffect(() => {
    if (!loading && user && !oauthMode) window.location.replace(redirectTo);
  }, [loading, user, oauthMode, redirectTo]);

  // Parse OAuth params if present (mode=oauth)
  const oauthParams = useMemo((): OAuthParams | null => {
    if (searchParams.get('mode') !== 'oauth') return null;

    const oauthCallback = searchParams.get('oauth_callback');
    const clientId = searchParams.get('client_id');
    const codeChallenge = searchParams.get('code_challenge');
    const redirectUri = searchParams.get('redirect_uri');

    if (!oauthCallback || !clientId || !codeChallenge || !redirectUri) return null;

    return {
      oauthCallback,
      clientId,
      codeChallenge,
      redirectUri,
      state: searchParams.get('state') || undefined,
    };
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' as const },
    },
  };

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />

      <div className="relative flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="w-full max-w-sm space-y-6"
        >
          {/* Logo */}
          <div className="flex justify-center">
            <Logo variant="modern" />
          </div>

          {/* Login Form */}
          <LoginForm
            oauthParams={oauthParams}
            onSuccess={() => {
              // In OAuth mode, LoginForm handles the redirect itself
              if (oauthParams) return;

              console.log('Login successful, redirecting to:', redirectTo);
              // Navigate to the originally requested page (or home)
              window.location.href = redirectTo;
            }}
            onError={(error) => {
              console.error('Login error:', error);
            }}
          />

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} 4K Labs
          </p>
        </motion.div>
      </div>
    </div>
  );
};
